
import React, { useCallback, useEffect, useState } from "react";
import BeginnerSidebar from "./BeginnerSidebar";
import { djangoApi } from "../api/auth";
import "../styles/practice.css";
import "../styles/BeginnerButtons.css";

const apiError = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  if (data?.detail) return data.detail;

  if (data && typeof data === "object") {
    return Object.entries(data)
      .map(([key, value]) =>
        `${key}: ${Array.isArray(value) ? value.join(", ") : String(value)}`
      )
      .join(" | ");
  }

  return error?.message || "Something went wrong. Please try again.";
};

const unwrap = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return data;
};

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const isDevelopment = process.env.NODE_ENV !== "production";

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existing = document.querySelector(
      'script[data-razorpay-checkout="true"]'
    );

    if (existing) {
      existing.addEventListener("load", () => resolve(true), {
        once: true,
      });
      existing.addEventListener(
        "error",
        () => reject(new Error("Razorpay checkout could not load.")),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.dataset.razorpayCheckout = "true";
    script.onload = () => resolve(true);
    script.onerror = () =>
      reject(
        new Error(
          "Could not load Razorpay checkout. Check your internet connection."
        )
      );

    document.body.appendChild(script);
  });
}

export default function BeginnerSubscription() {
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [testingId, setTestingId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const results = await Promise.allSettled([
        djangoApi.get("/api/beginner/subscription-plans/"),
        djangoApi.get("/api/beginner/my-subscription/"),
      ]);

      if (results[0].status === "fulfilled") {
        setPlans(unwrap(results[0].value) || []);
      } else {
        throw results[0].reason;
      }

      if (results[1].status === "fulfilled") {
        const data = unwrap(results[1].value);
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : null;

        // Choose the newest active subscription, if one exists.
        const current = items
          ? items.find(
              (item) =>
                String(item.status).toUpperCase() === "ACTIVE" &&
                (!item.expires_at ||
                  new Date(item.expires_at) > new Date())
            ) || null
          : data?.subscription || data || null;

        setSubscription(current);
      } else if (results[1].reason?.response?.status === 404) {
        setSubscription(null);
      } else {
        throw results[1].reason;
      }
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const purchase = async (plan) => {
    setError("");
    setMessage("");
    setPayingId(plan.id);

    try {
      await loadRazorpay();

      const orderResponse = await djangoApi.post(
        "/api/beginner/subscription-orders/",
        { plan_id: plan.id, ...(couponCode.trim() ? { coupon_code: couponCode.trim() } : {}) }
      );

      const order = orderResponse.data;

      if (!order?.key_id || !order?.order_id || !order?.payment_record_id) {
        throw new Error(
          "The server did not return the required Razorpay order details."
        );
      }

      const checkout = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Beginner Examination",
        description: `${plan.name} subscription`,
        prefill: order.student || {},
        notes: {
          payment_record_id: String(order.payment_record_id),
          plan_id: String(plan.id),
        },
        theme: { color: "#152744" },

        handler: async (response) => {
          try {
            const verify = await djangoApi.post(
              "/api/beginner/subscription-payments/verify/",
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            );

            setMessage(
              verify.data?.detail ||
                "Payment verified. Your subscription is active."
            );

            await loadData();
          } catch (e) {
            setError(
              `Payment was returned by checkout, but server verification failed: ${apiError(
                e
              )} Contact the administrator before paying again.`
            );
          } finally {
            setPayingId(null);
          }
        },

        modal: {
          ondismiss: () => setPayingId(null),
        },
      });

      checkout.on("payment.failed", (event) => {
        setError(
          event?.error?.description || "Payment failed. You can try again."
        );
        setPayingId(null);
      });

      checkout.open();
    } catch (e) {
      setError(apiError(e));
      setPayingId(null);
    }
  };

  const testActivate = async (plan) => {
    const confirmed = window.confirm(
      `Activate "${plan.name}" as a DEVELOPMENT TEST subscription? No payment will be made.`
    );

    if (!confirmed) return;

    setError("");
    setMessage("");
    setTestingId(plan.id);

    try {
      const response = await djangoApi.post(
        "/api/beginner/test-activate-subscription/",
        { plan_id: plan.id }
      );

      setMessage(
        response.data?.detail ||
          "Development test subscription activated."
      );

      await loadData();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setTestingId(null);
    }
  };

  const active =
    subscription &&
    String(subscription.status).toUpperCase() === "ACTIVE" &&
    (!subscription.expires_at ||
      new Date(subscription.expires_at) > new Date());

  return (
    <div className="beginner-portal">
      <BeginnerSidebar />

      <main className="beginner-portal-content">
        <header className="beginner-page-header">
          <div>
            <p className="beginner-eyebrow">BEGINNER STUDENT PORTAL</p>
            <h1>Subscription</h1>
            <p>View your access and choose a plan for your class.</p>
          </div>

          <button
            className="beginner-btn beginner-btn-secondary beginner-refresh-btn"
            type="button"
            onClick={loadData}
            disabled={loading || payingId !== null || testingId !== null}
          >
            Refresh
          </button>
        </header>

        {isDevelopment && (
          <div className="beginner-subscription-status">
            <i className="bi bi-tools" />
            <span>
          
            </span>
          </div>
        )}

        {error && (
          <div className="beginner-practice-error" role="alert">
            {error}
          </div>
        )}

        {message && (
          <div className="beginner-subscription-status" role="status">
            {message}
          </div>
        )}

        <section className="beginner-practice-card">
          <h2>Your Subscription</h2>

          {loading ? (
            <p>Loading subscription details…</p>
          ) : active ? (
            <div className="beginner-subscription-status">
              <i className="bi bi-check-circle" />
              <span>
                <strong>
                  {subscription.plan_name ||
                    subscription.name ||
                    subscription.plan?.name ||
                    "Active plan"}
                </strong>
                {" — Active"}
                {subscription.expires_at
                  ? ` until ${new Date(
                      subscription.expires_at
                    ).toLocaleDateString()}`
                  : ""}
              </span>
            </div>
          ) : (
            <div className="beginner-subscription-status">
              <i className="bi bi-info-circle" />
              <span>
                No active subscription was found. Choose an available plan
                below.
              </span>
            </div>
          )}
        </section>

        <section className="beginner-practice-card">
          <h2>Available Plans</h2>

          {loading ? (
            <p>Loading plans…</p>
          ) : plans.length === 0 ? (
            <p>
              No active plans are available for purchase right now. Please
              contact the administrator.
            </p>
          ) : (
            <div className="beginner-subscription-plans">
              <div className="coupon-entry" style={{ margin: "16px 0" }}>
        <label htmlFor="beginner-subscription-coupon">Coupon code</label>
        <input id="beginner-subscription-coupon" value={couponCode} onChange={(event) => setCouponCode(event.target.value.toUpperCase())} placeholder="Enter coupon code" autoComplete="off" />
        <small>Discount will be validated by the server before payment.</small>
      </div>
      {plans.map((plan) => (
                <article
                  className="beginner-subscription-plan"
                  key={plan.id}
                >
                  {plan.is_featured && <p><strong>Featured plan</strong></p>}

                  <h3>{plan.name}</h3>

                  {plan.description && <p>{plan.description}</p>}

                  <p><strong>{money(plan.price)}</strong></p>
                  <p>{plan.duration_days} days of access</p>
                  <p>
                    Eligible classes: {plan.minimum_class}–
                    {plan.maximum_class}
                  </p>

                  {plan.practice_set_limit != null && (
                    <p>Practice-set limit: {plan.practice_set_limit}</p>
                  )}

                  {plan.main_exam_attempt_limit != null && (
                    <p>
                      Main-exam attempt limit: {plan.main_exam_attempt_limit}
                    </p>
                  )}

                  <button
                    className="beginner-btn beginner-btn-primary beginner-subscribe-btn"
                    type="button"
                    onClick={() => purchase(plan)}
                    disabled={
                      payingId !== null ||
                      testingId !== null ||
                      loading
                    }
                  >
                    {payingId === plan.id
                      ? "Opening secure checkout…"
                      : `Subscribe for ${money(plan.price)}`}
                  </button>

                  {isDevelopment && (
                    <button
                      type="button"
                      className="beginner-test-activate beginner-btn beginner-btn-outline"
                      onClick={() => testActivate(plan)}
                      disabled={
                        payingId !== null ||
                        testingId !== null ||
                        loading
                      }
                    >
                      {testingId === plan.id
                        ? "Activating test subscription…"
                        : "Test Activate (No Payment)"}
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}

          <p className="beginner-subscription-note">
            Real purchases are confirmed by the server through Razorpay.
            Test activation is for local development only and does not
            represent a completed payment.
          </p>
        </section>
      </main>
    </div>
  );
}
